import gsap from 'gsap';
import Swiper from 'swiper';
import { module as Module } from 'modujs';
import { FONT } from '../config';
import { whenReady } from '../utils/fonts';
import { $screenDebounce } from '../stores/screen';
import { Navigation } from 'swiper/modules';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(SplitText);

export default class Blockquote extends Module {
    static MQ_SM = `(max-width: 699px)`;
    static PREV_CLASS = 'is-prev';
    static ACTIVE_CLASS = 'is-active';

    constructor(m) {
        super(m);

        // Binding
        this.onResize = this.onResize.bind(this);
        this.onPrevClick = this.onPrevClick.bind(this);
        this.onNextClick = this.onNextClick.bind(this);

        // UI
        this.$swiper = this.$('swiper')[0];
        this.$prevButton = this.$('prev')[0];
        this.$nextButton = this.$('next')[0];
        this.$contents = Array.from(this.$('content'));
        this.$items = Array.from(this.$('item'));
        this.$slides = Array.from(this.$('slide'));

        // Data
        this.activeIndex = -1;
        this.prevIndex = -2;
    }

    // =============================================================================
    // Lifecycle
    // =============================================================================
    init() {
        whenReady(FONT.EAGER).then((fonts) => this.onFontsLoaded(fonts));
        this.bindEvents();

        requestAnimationFrame(() => {
            this.setCarousel();
            this.reSplit();
            this.computeMaxHeight();
        });
    }

    destroy() {
        super.destroy();
        this.unbindEvents();
    }

    // =============================================================================
    // Events
    // =============================================================================
    bindEvents() {
        this.unbindScreenListener = $screenDebounce.listen(this.onResize);
    }

    unbindEvents() {
        this.unbindScreenListener?.();
        this.destroyCustomCarousel();
        this.destroySwiper();
    }

    // =============================================================================
    // Callbacks
    // =============================================================================
    onFontsLoaded(fonts) {
        this.setCarousel();
        this.reSplit();
        this.computeMaxHeight();
    }

    onResize({ width, height }) {
        this.setCarousel();
        this.reSplit();
        this.computeMaxHeight();
    }

    onPrevClick() {
        this.setActive(-1);
    }

    onNextClick() {
        this.setActive(1);
    }

    // =============================================================================
    // Methods
    // =============================================================================
    setCarousel() {
        if (window.matchMedia(`${Blockquote.MQ_SM}`).matches && !this.swiper) {
            this.initSwiper();
            this.destroyCustomCarousel();
        } else if (!window.matchMedia(`${Blockquote.MQ_SM}`).matches && this.swiper) {
            this.destroySwiper();
            this.initCustomCarousel();
        } else if (!window.matchMedia(`${Blockquote.MQ_SM}`).matches) {
            this.initCustomCarousel();
        }
    }

    initSwiper() {
        this.resetSplit();

        this.swiper = new Swiper(this.$swiper, {
            modules: [Navigation],
            speed: 400,
            loop: true,
            navigation: {
                prevEl: this.$prevButton,
                nextEl: this.$nextButton
            }
        });
    }

    destroySwiper() {
        this.swiper?.destroy(true, true);
        this.swiper = null;
    }

    initCustomCarousel() {
        this.$prevButton?.addEventListener('click', this.onPrevClick);
        this.$nextButton?.addEventListener('click', this.onNextClick);

        /* Reset */
        this.activeIndex = -1;
        this.prevIndex = -2;
        this.setActive(1);
    }

    destroyCustomCarousel() {
        this.$prevButton?.removeEventListener('click', this.onPrevClick);
        this.$nextButton?.removeEventListener('click', this.onNextClick);
    }

    split() {
        this.splitObject = new SplitText(this.$contents, {
            type: 'words',
            tag: 'span'
        });

        const wordByItems = [];
        this.splitObject.words.forEach((word, i) => {
            this.$items.forEach(($item, itemsIndex) => {
                if ($item.contains(word)) {
                    if (!wordByItems[itemsIndex]) {
                        wordByItems[itemsIndex] = [];
                    }
                    wordByItems[itemsIndex].push(word);
                }
            });
        });

        wordByItems.forEach((words) => {
            words.forEach((word, wordIndex) => {
                word.style.setProperty('--word-index', wordIndex);
            });
        });
    }

    reSplit() {
        if (this.swiper) return;

        this.splitObject?.revert?.();

        requestAnimationFrame(() => {
            this.split();
        });
    }

    resetSplit() {
        this.splitObject?.revert?.();
    }

    computeMaxHeight() {
        if (this.swiper) return;
        this.el.style.setProperty('--slide-height', `auto`);

        requestAnimationFrame(() => {
            const maxHeight = Math.max(...this.$items.map(($item) => $item.offsetHeight));
            this.el.style.setProperty('--slide-height', `${maxHeight}px`);
        });
    }

    setActive(increment) {
        this.$slides[this.prevIndex]?.classList.remove(Blockquote.PREV_CLASS);

        this.activeIndex = (this.activeIndex + increment) % this.$slides.length;
        this.activeIndex = this.activeIndex < 0 ? this.$slides.length - 1 : this.activeIndex;
        this.prevIndex = this.activeIndex === 0 ? this.$slides.length - 1 : this.activeIndex - 1;

        if (this.$slides.length > 1) {
            for (const $slide of this.$slides) {
                $slide.classList.remove(Blockquote.ACTIVE_CLASS);
            }
            this.$slides[this.activeIndex]?.classList.remove(Blockquote.PREV_CLASS);

            this.$slides[this.prevIndex]?.classList.add(Blockquote.PREV_CLASS);
        }

        requestAnimationFrame(() => {
            this.$slides[this.activeIndex]?.classList.add(Blockquote.ACTIVE_CLASS);
        });
    }
}
